import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { DayPlan } from '@shared/types';

interface ProgressStripProps {
  plan: DayPlan;
  /** Точность за 7 дней, %; бэкенд отдаёт 100 и без ответов — поэтому есть ли тренировки на неделе. */
  accuracy: number;
  trainedThisWeek: boolean;
  phone: boolean;
}

/**
 * «Твой прогресс» вверху «Слов и модулей» (2026-10-23; до того — карточка на главной): сколько слов из модулей уже
 * пройдено хотя бы раз, сколько сегодня и точность за 7 дней — одной полосой, чтобы модули были видны сразу. Всё — по
 * истории тренировок (`plan`) и законченным тренировкам (`accuracy`); FSRS-поля сводки застыли с 2026-09-10 и сюда не
 * идут (notes.md).
 */
export function ProgressStrip({ plan, accuracy, trainedThisWeek, phone }: ProgressStripProps) {
  const { t } = useTranslation();
  const passed = Math.max(0, plan.ready - plan.new);
  const share = plan.ready > 0 ? Math.round((passed / plan.ready) * 100) : 0;
  // Телефон — одной строкой под заголовком (вариант B, 2026-10-28): полка модулей и «Слова» на первом экране.
  if (phone) {
    return (
      <p className="mt-2 text-[13px] leading-[18px] text-muted">
        {plan.ready > 0 ? (
          <>
            <b className="font-semibold tabular-nums text-[#cfcac3]">{t('words.progress.line', { passed, count: plan.ready })}</b>
            {trainedThisWeek && ` · ${t('words.progress.lineAccuracy', { value: accuracy })}`}
          </>
        ) : (
          t('words.progress.empty')
        )}
      </p>
    );
  }
  return (
    <section
      aria-labelledby="words-progress-title"
      className={cn(
        'flex flex-wrap items-center gap-x-6 gap-y-2.5 rounded-card border border-solid border-line bg-card',
        phone ? 'px-4 py-3.5' : 'px-6 py-4',
      )}
    >
      <h2 id="words-progress-title" className="w-full text-[12px] font-semibold uppercase leading-4 tracking-[0.14em] text-muted sm:w-auto">
        {t('words.progress.title')}
      </h2>
      {plan.ready > 0 ? (
        <>
          <p className="flex flex-wrap items-baseline gap-x-1.5 text-fg">
            <span className="font-serif text-[24px] font-medium leading-none tabular-nums">{passed}</span>
            <span className="text-[15px]">{t('words.progress.passed', { count: passed })}</span>
            <span className="text-[14px] text-muted">{t('words.progress.ofReady', { count: plan.ready })}</span>
          </p>
          <span className="block h-1.5 min-w-[96px] flex-1 overflow-hidden rounded-pill bg-line" aria-hidden>
            <span className="block h-full rounded-pill bg-accent transition-[width] duration-500" style={{ width: `${share}%` }} />
          </span>
          <dl className="flex gap-5 text-[14px] leading-5">
            <div className="flex gap-1.5">
              <dt className="text-muted">{t('words.progress.today')}</dt>
              <dd className="font-semibold tabular-nums text-fg">{t('training.words', { count: plan.doneToday })}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-muted">{t('words.progress.accuracy')}</dt>
              <dd className="font-semibold tabular-nums text-fg">{trainedThisWeek ? `${accuracy}%` : '—'}</dd>
            </div>
          </dl>
        </>
      ) : (
        <p className="text-[14px] leading-5 text-muted">{t('words.progress.empty')}</p>
      )}
    </section>
  );
}
